import React, { CSSProperties } from "react";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { EntityIntelligenceResponse } from "@/features/orders-analytics/types/orders-analytics.types";
import { formatters } from "@/features/orders-analytics/utils/formatters";

type Props = {
    data: EntityIntelligenceResponse;
};

const tiles = (data: EntityIntelligenceResponse): { label: string; value: string; sub: string }[] => [
    {
        label: "Real customers",
        value: formatters.formatNumber(data.realCustomers),
        sub: "distinct people (entities)",
    },
    {
        label: "Operations / customer",
        value: formatters.formatNumber(data.operationsPerCustomer),
        sub: "orders + deposits in range",
    },
    {
        label: "Multi-account",
        value: formatters.formatPercentage(data.multiAccountShare),
        sub: `${formatters.formatNumber(data.multiAccountCustomers)} people with >1 account`,
    },
    {
        label: "Identity coverage",
        value: formatters.formatPercentage(data.identityCoverage),
        sub: `${formatters.formatNumber(data.linkedAccounts)} linked · ${formatters.formatNumber(data.unlinkedAccounts)} unlinked`,
    },
];

const maxTier = (data: EntityIntelligenceResponse): number =>
    data.frequencyTiers.reduce((max, tier) => (tier.entities > max ? tier.entities : max), 0);

const warnStyle: CSSProperties = {
    padding: 16,
    borderLeft: "3px solid var(--vb-warning-500)",
    fontSize: 12,
    color: "var(--vb-fg-muted)",
    lineHeight: 1.55,
};

export const EntityIntelligenceSection: React.FC<Props> = ({ data }) => {
    const top = maxTier(data);

    return (
        <section className="section">
            <SectionHeader
                title="Real customers (people)"
                meta="the person who orders — deduped across accounts via entity (entities-read)"
            />

            <div className="grid-4">
                {tiles(data).map((tile) => (
                    <div key={tile.label} className="card tile">
                        <div className="tile__label">{tile.label}</div>
                        <div className="tile__value num">{tile.value}</div>
                        <div className="tile__sub">{tile.sub}</div>
                    </div>
                ))}
            </div>

            <div className="grid-12" style={{ marginTop: 12 }}>
                <div style={{ gridColumn: "span 7" }}>
                    <SectionHeader title="Activity tiers" meta="customers by lifetime operations" />
                    <div className="card ci-table-card">
                        <div className="ci-scroll">
                            <table className="ci-tbl">
                                <thead>
                                    <tr>
                                        <th scope="col">Operations</th>
                                        <th scope="col" className="num">Customers</th>
                                        <th scope="col">Share</th>
                                        <th scope="col" className="num">Operations</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.frequencyTiers.map((tier) => (
                                        <tr key={tier.label}>
                                            <td className="num" style={{ fontWeight: 600 }}>{tier.label}</td>
                                            <td className="num">{formatters.formatNumber(tier.entities)}</td>
                                            <td>
                                                <div className="ci-bar">
                                                    <div className="ci-bar__track">
                                                        <div
                                                            className="ci-bar__fill"
                                                            style={{ width: `${top > 0 ? (tier.entities / top) * 100 : 0}%` }}
                                                        />
                                                    </div>
                                                    <span className="ci-bar__value">
                                                        {formatters.formatPercentage(tier.entityShare)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="num">{formatters.formatNumber(tier.operations)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div style={{ gridColumn: "span 5" }}>
                    <SectionHeader title="Where they are" meta="customers by country" />
                    <div className="card ci-table-card">
                        <div className="ci-scroll">
                            <table className="ci-tbl">
                                <thead>
                                    <tr>
                                        <th scope="col">Country</th>
                                        <th scope="col" className="num">Customers</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.topCountries.map((row) => (
                                        <tr key={row.country}>
                                            <td style={{ fontWeight: 600 }}>{row.country}</td>
                                            <td className="num">{formatters.formatNumber(row.customers)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card" style={warnStyle}>
                <b>Lectura de identidad:</b> solo el{" "}
                {formatters.formatPercentage(data.identityCoverage)} de las cuentas que operan
                enlazan a una entidad (persona) en entities-read; el resto no tiene identidad
                proyectada. Además casi nadie tiene varias cuentas porque la identidad legal viene
                con placeholder (taxId/kycId 123123) — el dedup real mejorará al corregir la
                identidad en origen.
            </div>
        </section>
    );
};
